'use client'
import {
  GetProductFilterListByActivityIdQuery,
  GetProductFilterListByActivityIdQueryVariables,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import React, { useState } from 'react'
import { useGraphqlInfinite } from '@dustin-web/microsite-data/src/hooks/use-graphql-infinite'
import { isNotNullOrUndefined } from '@dustin-web/microsite-data/src/utils'
import { useQueryState, useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import { FacetList } from '../filters/facet-list'
import { BOOLEAN_FACET_NAME } from '../filters'
import { getSortBy, ProductListOptions, useProductListSettings } from '../product-list'
import { GET_PRODUCT_FILTER_LIST_BY_ACTIVITY_ID } from '@dustin-web/microsite-data/src/graphql/product/get-product-filter-list-by-activity-id'
import { useLayout } from '@dustin-web/microsite-components/src/hooks'
import { Drawer } from '@dustin-web/microsite-components/src/drawer'
import LazyHydrate from '@dustin-web/microsite-components/src/utils/lazy-hydrate'
import Head from 'next/head'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import { ProductInfinityList } from '../product-list/product-infinity-list'

type Props = {
  activityId: number
  header?: string
  hideFacets: boolean
  showOnlyBasicFacets: boolean
}

export const CampaignProductList = ({
  activityId,
  header,
  hideFacets,
  showOnlyBasicFacets,
}: Props) => {
  const { queryState, setQueryState } = useQueryState()
  const { siteUrl } = useSiteContext()
  const { responsive } = useLayout()
  const { activeFilters: filtersFromUser } = queryState
  const [showMobileDrawer, setShowMobileDrawer] = useState(false)
  const { pageUriWithoutQuery } = useCleanRouter()
  const { sortBy, pageSize } = useProductListSettings()
  const [page, setPage] = useState(queryState.page)

  const { data, size, setSize, isValidating, error } = useGraphqlInfinite<
    GetProductFilterListByActivityIdQuery,
    GetProductFilterListByActivityIdQueryVariables
  >(GET_PRODUCT_FILTER_LIST_BY_ACTIVITY_ID, {
    variables: {
      page,
      pageSize,
      facets: filtersFromUser,
      activityId,
      sortBy: getSortBy(sortBy),
    },
    swrOptions: {
      initialSize: page,
      revalidateFirstPage: false,
      persistSize: false,
    },
  })

  const lastPage = data?.filter(isNotNullOrUndefined)?.[data.length - 1]

  const totalHits = lastPage?.activity?.productFilterList.hits || 0
  const totalPages = totalHits ? Math.ceil(totalHits / pageSize) : null

  if (!data && !Array.isArray(data)) {
    return null
  }

  const isLoadingInitialData = !data && !error

  const productReferences = data
    .map(d => d.activity?.productFilterList.productReferences)
    .filter(isNotNullOrUndefined)

  const firstPageData = data[0]
  const isLoadingMore = isLoadingInitialData || isValidating

  const promotedBanner = firstPageData?.activity?.banners?.find(
    x => x?.displayMode === 'PromotedProduct'
  )

  const promotedProduct =
    promotedBanner && promotedBanner.relatedProducts && promotedBanner.relatedProducts.length > 0
      ? {
          ...promotedBanner.relatedProducts[0]!,
          campaignTitle: promotedBanner.text || '',
          imageUrl: promotedBanner.imageUrl || '',
        }
      : null

  const facets =
    firstPageData?.activity?.productFilterList.facets?.filter(isNotNullOrUndefined) || []
  const digitalDataOrigin = 'CampaignPage'

  const clearFilters = () => {
    const keys = filtersFromUser.reduce((acc, activeFilter) => {
      acc[activeFilter.name] = ''
      return acc
    }, {} as { [key: string]: string })

    keys[BOOLEAN_FACET_NAME] = ''
    keys.page = ''

    setQueryState(keys)
  }

  const filteredFacets = !hideFacets
    ? showOnlyBasicFacets
      ? facets.filter(facet =>
          ['manufacturer', 'price', 'isnewproduct', 'campaign', 'instock'].includes(facet.nameInUrl)
        )
      : facets
    : []

  const previousPageUrl = page > 1 ? `${siteUrl}${pageUriWithoutQuery}${`?page=${page - 1}`}` : null
  const nextPagePath = `${pageUriWithoutQuery}${`?page=${page + 1}`}`

  const nextPageUrl =
    totalPages && page < totalPages && nextPagePath ? siteUrl + nextPagePath : null

  return (
    <>
      <Head>
        {previousPageUrl && <link rel="prev" href={previousPageUrl} />}
        {nextPageUrl && <link rel="next" href={nextPageUrl} />}
      </Head>
      <div className="row">
        <div className="col-12 col-sm-6">
          {header && <h2 className=" d-none d-sm-block">{header}</h2>}
        </div>
        <div className="col-12 col-sm-6">
          <div className="d-sm-flex justify-content-end">
            <ProductListOptions
              disablePageSize
              disableSearch
              disableListType
              onMobileFilterOverlayOpen={() => setShowMobileDrawer(true)}
            />
          </div>
        </div>
      </div>
      <div className="row">
        {!hideFacets && !responsive.isSmallScreen && (
          <div className="col-12 col-lg-3">
            <div className="u-pt-8">
              <FacetList facets={filteredFacets} onClearFilter={() => clearFilters()} />
            </div>
          </div>
        )}
        <div className="col-12 col-lg-9">
          <div className="u-mb-16">
            <ProductInfinityList
              productReferences={productReferences}
              promotedProduct={promotedProduct || null}
              isLoadingMore={isLoadingMore}
              digitalDataOrigin={digitalDataOrigin}
              totalHits={totalHits}
              viewTypeOverride={!responsive.breakpoints.max.SM ? 'gallery' : 'extended'}
              handleLoadMore={() => {
                setSize(size + 1)
                setPage(size + 1)
              }}
              page={page}
            />
          </div>
        </div>
        <Drawer
          visible={showMobileDrawer && responsive.isSmallScreen}
          onClose={() => setShowMobileDrawer(false)}
          className="row no-gutters u-px-0 u-py-0">
          <div className="col-12 col-lg-3 u-h-100">
            <LazyHydrate whenVisible className="d-flex flex-column u-h-100">
              <FacetList
                facets={facets || []}
                totalHits={totalHits}
                onClearFilter={() => clearFilters()}
                onClose={() => setShowMobileDrawer(false)}
              />
            </LazyHydrate>
          </div>
        </Drawer>
      </div>
    </>
  )
}

export default CampaignProductList
